// The client half of the exam-PDF viewer: where a source paper is read from,
// and what it's called once saved.
//
// Nothing here fetches the publisher directly. `quiz/api/exam-pdf.js` re-serves the
// file from our own origin, because the examining bodies send no CORS headers,
// may refuse to be framed, and can't be saved through a cross-origin
// `<a download>`. This module only builds the URLs and keeps the same
// allowlist the endpoint enforces, so an unsupported source is refused before a
// viewer is opened rather than after a request round-trips.

/**
 * Hosts whose PDFs the endpoint will serve. Mirrors `DEFAULT_HOSTS` in
 * `quiz/api/exam-pdf.js`.
 *
 * Wider than the exams alone: every PDF a resource page's `Available from`
 * names is read in the same viewer, so every publisher the vault links a PDF
 * on belongs here too — the standards board's ASOPs, a paper on its author's
 * university site, a regulator's or an industry body's report. A PDF on a host
 * missing from this list shows up as a card that can't be read in the app;
 * `examPdf.test.ts` holds every resource page to it. Adding a host here without
 * adding it there opens a viewer the endpoint then refuses.
 */
export const EXAM_PDF_HOSTS = [
  'casact.org',
  'www.casact.org',
  'soa.org',
  'www.soa.org',
  'actuarialstandardsboard.org',
  'www.actuarialstandardsboard.org',
  // Gelman & Unwin's manuscript, on the Columbia statistics department's site
  // (`www.` redirects to `sites.`, and the endpoint re-checks where it lands).
  'stat.columbia.edu',
  'www.stat.columbia.edu',
  'sites.stat.columbia.edu',
  // The Institute and Faculty of Actuaries' GIRO working-party paper.
  'actuaries.org.uk',
  'www.actuaries.org.uk',
  // MSA Research's legend of P&C KPI definitions.
  'msaresearch.com',
  'www.msaresearch.com',
  // PACICC's report on the actuary's role in safeguarding solvency.
  'pacicc.ca',
  'www.pacicc.ca',
  // The Canadian Institute of Actuaries' syllabi — through the one download
  // endpoint below, never any other path on the host.
  'www.cia-ica.ca',
]

/**
 * A publisher that serves its PDFs through a download script rather than at a
 * `.pdf` path: the host and the script's exact path. The CIA's publication
 * pages link every document as `dl_file.php?p=…&fid=…`, the FCIA and ACIA
 * syllabi among them. Mirrors `PDF_DOWNLOAD_ENDPOINTS` in
 * `quiz/api/exam-pdf.js`; the endpoint still refuses a response that isn't a
 * PDF.
 */
export const PDF_DOWNLOAD_ENDPOINTS: readonly { host: string; path: string }[] = [
  { host: 'www.cia-ica.ca', path: '/app/themes/wicket/custom/dl_file.php' },
]

/** A `.pdf` path, or a publisher's download script named in `PDF_DOWNLOAD_ENDPOINTS`. */
function isPdfPath(parsed: URL): boolean {
  if (/\.pdf$/i.test(parsed.pathname)) return true
  const host = parsed.hostname.toLowerCase()
  return PDF_DOWNLOAD_ENDPOINTS.some(e => e.host === host && e.path === parsed.pathname)
}

/** Can this URL be shown in the viewer? https, a `.pdf` (or a known download script), on a publisher we proxy. */
export function isSupportedPdfSource(url: string): boolean {
  try {
    const parsed = new URL(url)
    return (
      parsed.protocol === 'https:' &&
      EXAM_PDF_HOSTS.includes(parsed.hostname.toLowerCase()) &&
      isPdfPath(parsed)
    )
  } catch {
    return false
  }
}

/**
 * The PDF a resource page's link field names, when the app can read it — the
 * `"[host](url)"` value of its `Available from`. `undefined` for a page whose
 * link is not a PDF (a publisher's landing page, a catalogue search) or names
 * none.
 *
 * What a resource card's PDF mark and the page's **Read PDF** both rest on, so
 * the shelf never promises a document the page can't open.
 */
export function resourcePdfUrl(link: unknown): string | undefined {
  if (typeof link !== 'string') return undefined
  const value = link.trim()
  const url = value.match(/\(([^)]+)\)/)?.[1] ?? (value.startsWith('http') ? value : undefined)
  return url && isSupportedPdfSource(url) ? url : undefined
}

/** The proxy endpoint. Overridable for a split deployment, as `VITE_PASS_RATES_URL` is. */
export function examPdfEndpoint(): string {
  return import.meta.env.VITE_EXAM_PDF_URL || '/api/exam-pdf'
}

/** Where the viewer reads the document's bytes from. */
export function pdfProxyUrl(sourceUrl: string): string {
  return `${examPdfEndpoint()}?url=${encodeURIComponent(sourceUrl)}`
}

/** The same file, served as an attachment so the browser saves it. */
export function pdfDownloadUrl(sourceUrl: string): string {
  return `${examPdfEndpoint()}?url=${encodeURIComponent(sourceUrl)}&download=1`
}

/** The file's own name (`sp19-5.pdf`), used as the download filename. */
export function pdfFileName(sourceUrl: string): string {
  try {
    const name = new URL(sourceUrl).pathname.split('/').pop()
    return name && /\.pdf$/i.test(name) ? name : 'exam.pdf'
  } catch {
    return 'exam.pdf'
  }
}

/** The first bytes of every PDF. Anything else is not one, whatever it claims. */
const PDF_MAGIC = '%PDF-'

export function looksLikePdf(bytes: Uint8Array): boolean {
  if (bytes.length < PDF_MAGIC.length) return false
  for (let i = 0; i < PDF_MAGIC.length; i++) {
    if (bytes[i] !== PDF_MAGIC.charCodeAt(i)) return false
  }
  return true
}

/**
 * A reader-facing sentence for a response that came back without a PDF in it.
 *
 * The case worth naming precisely is HTML: the app is a single-page app whose
 * host rewrites unknown paths to `index.html`, so a deployment missing the
 * endpoint answers **200 with the app's own page**. Handed to a PDF parser that
 * reads as "invalid structure", which sends whoever debugs it hunting for a
 * corrupt file instead of a missing function.
 */
export function describeNonPdfResponse(status: number, contentType: string, body: string): string {
  const type = contentType.toLowerCase()
  const trimmed = body.trimStart()

  if (type.includes('text/html') || trimmed.startsWith('<!') || trimmed.startsWith('<html')) {
    return "the PDF service isn't available on this deployment"
  }
  if (type.includes('json')) {
    try {
      const parsed = JSON.parse(body) as { error?: unknown }
      if (typeof parsed.error === 'string' && parsed.error) return parsed.error
    } catch {
      /* not the JSON it claimed to be — fall through */
    }
  }
  if (status < 200 || status >= 300) return `the service responded ${status}`
  return 'the response was not a PDF'
}

/** The publisher, for the "where did this come from" line under the viewer. */
export function pdfSourceHost(sourceUrl: string): string {
  try {
    return new URL(sourceUrl).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

/**
 * The modifier state of a click, as far as "does this open a new tab" goes.
 * Taken as a plain shape rather than a `MouseEvent` so the rule is testable
 * without a DOM.
 */
export interface ClickModifiers {
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  button: number
}

/**
 * Does this click on a PDF link belong to the in-app reader?
 *
 * Every PDF button in the app is an anchor to the publisher underneath, so the
 * real URL stays visible and ⌘/ctrl-click, middle-click, shift-click and
 * long-press keep doing what a link does. A *plain* left click is the one the
 * reader takes — and only for a source `quiz/api/exam-pdf.js` will actually
 * serve, since opening a panel that can't load is worse than the tab.
 *
 * `linkOnly` is the host's veto, for a surface the reader can't paint above.
 */
export function opensInReader(url: string, click: ClickModifiers, linkOnly = false): boolean {
  if (linkOnly) return false
  if (click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) return false
  if (click.button !== 0) return false
  return isSupportedPdfSource(url)
}
